import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { EnvironmentService, SKIP_PROBLEM_HANDLER } from '@lib/shared/data-access';
import { DigitalMediaModel, DigitalMediaEntityType, DigitalMediaSecuredUrlModel } from '@lib/digital-media/models';

@Injectable({
    providedIn: 'root'
})
export class DigitalMediaService {

    private updateEvent = new Subject<any>();
    private httpClient = inject(HttpClient);
    private environmentService = inject(EnvironmentService);

    /**
     * An entity's media rows. Pass `silent` when the caller treats a failure as "no media" rather than
     * something to report — a mugshot lookup behind an avatar, whose fallback is the person's initials — so the
     * global problem interceptor re-throws to the caller instead of raising a toast the officer can do nothing
     * about. Omitted, the behaviour is unchanged for every existing caller.
     */
    getDigitalMediasByEntityIdAndEntityType(
        digitalMediaEntityId: number,
        digitalMediaEntityType: DigitalMediaEntityType,
        silent: boolean = false
    ): Observable<DigitalMediaModel[]> {
      if(digitalMediaEntityId === 0 || Number.isNaN(digitalMediaEntityId)) return of([]);
        return this.httpClient.get<DigitalMediaModel[]>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/entity/${digitalMediaEntityId}/type/${digitalMediaEntityType}`,
            silent ? { context: new HttpContext().set(SKIP_PROBLEM_HANDLER, true) } : {}
        );
    }

    uploadDigitalMedia(
        digitalMediaEntity: DigitalMediaEntityType,
        digitalMediaEntityId: number,
        formData: FormData,
        secondaryLinkEntityId: number = 0,
        secondaryLinkEntity: DigitalMediaEntityType = DigitalMediaEntityType.Unknown
    ): Observable<any> {
        return this.httpClient.post<any>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/entity/${digitalMediaEntityId}/type/${digitalMediaEntity}/linkEntity/${secondaryLinkEntityId}/linkEntityType/${secondaryLinkEntity}`,
            formData,
            {
                reportProgress: true,
                observe: 'events'
            }
        );
    }

    getDigitalMediaFileByIdAsync(mediaId: number, loadThumbnail: boolean = false): Observable<Blob> {
        return this.httpClient.get<any>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/${mediaId}/file?isThumbnail=${loadThumbnail}`,
            {
                responseType: 'blob' as 'json'
            }
        );
    }

    linkDigitalMediaWithEntity(digitalMedia: DigitalMediaModel, entityId: number, digitalMediaEntityType: DigitalMediaEntityType): Observable<any> {
        return this.httpClient.put<DigitalMediaModel[]>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/entity/${entityId}/type/${digitalMediaEntityType}/link`,
            digitalMedia
        );
    }

    unlinkDigitalMediaWithEntity(
        ids: number[],
        entityId: number,
        digitalMediaEntityType: DigitalMediaEntityType
    ): Observable<DigitalMediaModel[]> {
        return this.httpClient.put<DigitalMediaModel[]>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/entity/${entityId}/type/${digitalMediaEntityType}/unlink`,
            ids
        );
    }

    setDigitalMediaSequence(
        id: number,
        entityId: number,
        digitalMediaEntityType: DigitalMediaEntityType,
        digitalMedia: DigitalMediaModel
    ): Observable<DigitalMediaModel[]> {
        return this.httpClient.put<DigitalMediaModel[]>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/${id}/entity/${entityId}/type/${digitalMediaEntityType}/sequence`,
            digitalMedia
        );
    }

    getDigitalMediaURL(
        digitalMediaEntityId: number,
        digitalMediaEntityType: DigitalMediaEntityType
    ): string {
        return `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/entity/${digitalMediaEntityId}/type/${digitalMediaEntityType}/file/primary-image`;
    }

    /**
     * Fetches an entity's primary image as a Blob via the authenticated HttpClient (a raw cross-origin
     * &lt;img src&gt; to the digital-media host wouldn't carry the bearer token). The caller creates an object
     * URL from the blob. SKIP_PROBLEM_HANDLER is set so a missing image (404) is silent — the caller
     * treats any error as "no image" and falls back to initials, rather than showing an error toast.
     */
    getPrimaryImageBlob(
        digitalMediaEntityId: number,
        digitalMediaEntityType: DigitalMediaEntityType,
        loadThumbnail: boolean = true
    ): Observable<Blob> {
        if (!digitalMediaEntityId || Number.isNaN(digitalMediaEntityId) || digitalMediaEntityId <= 0) {
            return of(new Blob());
        }
        // `getThumbnail`, which is what the endpoint's parameter is actually called. It used to send
        // `isThumbnail`, which the endpoint does not bind — so the flag was silently ignored and the server's
        // own default (a thumbnail) applied no matter what the caller asked for. Defaulted to true here to keep
        // that behaviour for every existing caller, but it is now genuinely a choice.
        return this.httpClient.get(
            `${this.getDigitalMediaURL(digitalMediaEntityId, digitalMediaEntityType)}?getThumbnail=${loadThumbnail}`,
            {
                responseType: 'blob',
                context: new HttpContext().set(SKIP_PROBLEM_HANDLER, true)
            }
        );
    }

    triggerUpdateEvent(): void {
        this.updateEvent.next({});
    }

    getUpdateEvent(): Observable<any> {
        return this.updateEvent.asObservable();
    }

    // THIS NEEDS TO BE REMOVED AT SOME POINT DUE TO POTENTIAL SECURITY ISSUES
    public getDigitalMediaSecuredUrl(mediaId: number): Observable<DigitalMediaSecuredUrlModel> {
        return this.httpClient.get<DigitalMediaSecuredUrlModel>(
            `${this.environmentService.digitalMediaApiUrl}/api/v1/digital-media/${mediaId}/secured-urls`
        );
    }
}
